import React, { useState, useEffect } from 'react';
import { Heart, Loader2, Globe, Sparkles, Users } from 'lucide-react';
import api from '../utils/api';
import { SiteSettings } from '../types';

const AboutContent = () => {
    const [settings, setSettings] = useState<SiteSettings>({});
    const [loading, setLoading] = useState(true);

    // Palette Colors Constants
    const BRAND_CORAL = "#E84C5B";

    useEffect(() => {
        const fetchContent = async () => {
            try {
                const response = await api.get('/content/settings');
                if (response.data.success && response.data.data) {
                    setSettings(response.data.data);
                }
            } catch (error) {
                console.error("Error fetching About content", error);
            } finally {
                setLoading(false);
            }
        };
        fetchContent();
    }, []);

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center pt-20">
                <Loader2 className="w-10 h-10 text-brand animate-spin" />
            </div>
        );
    }

    return (
        <div className="pt-32 pb-20 animate-fade-in">

            {/* --- HERO SECTION --- */}
            <div className="max-w-7xl mx-auto px-6 mb-20">
                <div className="text-center max-w-3xl mx-auto">
                    <span className="text-brand font-bold text-sm tracking-widest uppercase mb-4 block">Who we are</span>
                    <h1 className="text-5xl md:text-6xl font-serif font-bold text-primary mb-6">
                        {settings.about_us_title || "Our Story"}
                    </h1>
                    <p className="text-xl text-gray-500 leading-relaxed">
                        Another Home Hospitality Services: Your Smart Partner in modern hospitality.
                    </p>
                </div>
            </div>

            {/* --- SPLIT SECTION --- */}
            <div className="max-w-7xl mx-auto px-6 mb-24">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                    <div className="relative h-[500px] rounded-[2.5rem] overflow-hidden shadow-2xl group">
                        {/* Nuova Immagine Funzionante (Interno Moderno/Lusso) */}
                        <img
                            src="about.jpg"
                            alt="Interior Design"
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                        />
                    </div>

                    <div className="lg:pl-8">
                        <h2 className="text-3xl font-serif font-bold text-gray-900 mb-6">
                            {settings.about_us_subtitle || "Bridging technology and comfort."}
                        </h2>
                        <div className="space-y-6 text-lg text-gray-600 leading-relaxed whitespace-pre-line">
                            {settings.about_us_description ||
                                "By combining the efficiency of smart technology with the warmth of traditional Italian hospitality, we offer a seamless experience for the modern traveler."}
                        </div>
                    </div>
                </div>
            </div>

            {/* --- MISSION BOX (The Quote) --- */}
            <div className="max-w-5xl mx-auto px-6 mb-24">
                <div className="rounded-[3rem] p-12 md:p-16 text-black text-center shadow-2xl relative overflow-hidden"  style={{backgroundColor: "#D6D1CB"}}>
                    <div className="relative z-10">
                        <div className="bg-white w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-8 backdrop-blur-md shadow-lg">
                            <Heart className="w-8 h-8 fill-brand text-white" />
                        </div>
                        <h3 className="text-3xl font-serif font-bold mb-6">Our Philosophy</h3>
                        <p className="text-xl md:text-2xl opacity-95 max-w-4xl mx-auto leading-relaxed font-light italic">
                            {settings.about_us_text ? `"${settings.about_us_text}"` : '"We don\'t just offer a place to sleep, but another home to live in."'}
                        </p>
                    </div>
                </div>
            </div>

            {/* --- VALUES GRID (Style: Tone on Tone) --- */}
            <div className="max-w-7xl mx-auto px-6">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-8">

                    {/* Item 1 */}
                    <div className="bg-gray-50 p-8 rounded-3xl text-center hover:bg-white hover:shadow-xl transition-all duration-300 border border-gray-100">
                        <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6" style={{ backgroundColor: `${BRAND_CORAL}1A` }}>
                            <Globe className="w-8 h-8" style={{ color: BRAND_CORAL }} />
                        </div>
                        <h4 className="font-bold text-xl mb-2 text-primary">Global Standards</h4>
                        <p className="text-gray-500">World-class amenities meeting international expectations.</p>
                    </div>

                    {/* Item 2 */}
                    <div className="bg-gray-50 p-8 rounded-3xl text-center hover:bg-white hover:shadow-xl transition-all duration-300 border border-gray-100">
                        <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6" style={{ backgroundColor: `${BRAND_CORAL}1A` }}>
                            <Sparkles className="w-8 h-8" style={{ color: BRAND_CORAL }} />
                        </div>
                        <h4 className="font-bold text-xl mb-2 text-primary">Premium Quality</h4>
                        <p className="text-gray-500">Impeccable cleanliness and high-end design in every property.</p>
                    </div>

                    {/* Item 3 */}
                    <div className="bg-gray-50 p-8 rounded-3xl text-center hover:bg-white hover:shadow-xl transition-all duration-300 border border-gray-100">
                        <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6" style={{ backgroundColor: `${BRAND_CORAL}1A` }}>
                            <Users className="w-8 h-8" style={{ color: BRAND_CORAL }} />
                        </div>
                        <h4 className="font-bold text-xl mb-2 text-primary">Human Touch</h4>
                        <p className="text-gray-500">Technology simplifies the process, but our team makes it special.</p>
                    </div>

                </div>
            </div>
        </div>
    );
};

export default AboutContent;